import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FileSpreadsheet, FileText, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { addDays, periodRanges, seriesColors, todayIn, type OrgDashboard } from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/misc';
import { ChartCard, DataTable as ChartTable, type Datum } from '@/features/dashboard/charts';
import {
  useAdminActivities,
  useAdminGroups,
  useAdminProjects,
  useAdminUsers,
  useOrgDashboard,
  useRequestReport,
} from '@/lib/admin-queries';
import { describeError } from '@/lib/errors';
import { formatHours, formatPercent } from '@/lib/format';
import { useMe } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { NewReportDialog } from './admin-reports-page';
import { AdminPage, DataTable, MultiSelect } from './admin-ui';

type Filters = {
  from?: string;
  to?: string;
  userIds?: string;
  groupIds?: string;
  projectIds?: string;
  activityIds?: string;
  tab?: 'overview' | 'users' | 'missing';
};

const list = (value?: string) => (value ? value.split(',').filter(Boolean) : []);
const joined = (ids: string[]) => (ids.length ? ids.join(',') : undefined);

/**
 * Organization dashboard (FR8). Every filter is in the URL, so "what I am looking at" is a
 * link — which is also what the drill-downs use: clicking a user or a project adds it to the
 * filters instead of navigating somewhere new.
 */
export function AdminOrgDashboardPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const search: Filters = useSearch({ from: '/app/admin/dashboard' });
  const { data: me } = useMe();
  const [reporting, setReporting] = useState(false);
  const requestReport = useRequestReport();

  const today = todayIn(me?.timeZone ?? 'UTC');
  const defaults = periodRanges('month', today, me?.organization.weekStartDay ?? 1).current;
  const from = search.from ?? defaults.from;
  const to = search.to ?? defaults.to;
  const tab = search.tab ?? 'overview';

  const query = {
    from,
    to,
    userIds: list(search.userIds),
    groupIds: list(search.groupIds),
    projectIds: list(search.projectIds),
    activityIds: list(search.activityIds),
  };
  /** Exactly what the dashboard is showing — handed unchanged to any export. */
  const reportFilters = {
    from,
    to,
    userIds: query.userIds.length ? query.userIds : undefined,
    groupIds: query.groupIds.length ? query.groupIds : undefined,
    projectIds: query.projectIds.length ? query.projectIds : undefined,
    activityIds: query.activityIds.length ? query.activityIds : undefined,
  };
  const dashboard = useOrgDashboard(reportFilters);

  const users = useAdminUsers({ pageSize: 200 });
  const groups = useAdminGroups({ pageSize: 200 });
  const projects = useAdminProjects({ pageSize: 200 });
  const activities = useAdminActivities({ pageSize: 200 });

  const setFilters = (next: Partial<Filters>) =>
    void navigate({ to: '/admin/dashboard', search: { ...search, from, to, ...next } });

  const preset = (period: 'week' | 'month' | 'lastMonth' | 'quarter') => {
    if (period === 'quarter') {
      setFilters({ from: addDays(today, -89), to: today });
      return;
    }
    // `periodRanges` already computes the range before the current one, which is exactly the
    // previous calendar month — first to last day, however long that month happened to be.
    const ranges = periodRanges(
      period === 'lastMonth' ? 'month' : period,
      today,
      me?.organization.weekStartDay ?? 1,
    );
    const range = period === 'lastMonth' ? ranges.previous : ranges.current;
    setFilters({ from: range.from, to: range.to });
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success(t('admin.org.linkCopied'));
  };

  return (
    <AdminPage
      title={t('admin.org.title')}
      description={t('admin.org.subtitle')}
      actions={
        <>
          <Button variant="outline" onClick={() => void copyLink()}>
            <Link2 className="h-4 w-4" aria-hidden="true" /> {t('admin.org.copyLink')}
          </Button>
          {/* Both exports carry the filters on screen, so the file matches what is shown. */}
          <Button
            variant="outline"
            onClick={() =>
              void requestReport.mutateAsync({ kind: 'entries', filters: reportFilters }).then(
                () => toast.success(t('admin.reports.queued')),
                (error: unknown) => toast.error(describeError(error, t)),
              )
            }
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" /> {t('admin.org.exportXlsx')}
          </Button>
          <Button onClick={() => setReporting(true)}>
            <FileText className="h-4 w-4" aria-hidden="true" /> {t('admin.org.generatePdf')}
          </Button>
        </>
      }
    >
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            {t('admin.org.from')}
            <Input
              type="date"
              value={from}
              onChange={(e) => e.target.value && setFilters({ from: e.target.value })}
              className="w-40"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            {t('admin.org.to')}
            <Input
              type="date"
              value={to}
              onChange={(e) => e.target.value && setFilters({ to: e.target.value })}
              className="w-40"
            />
          </label>
          {/* Wraps because four presets no longer fit a phone on one line. */}
          <div className="flex flex-wrap gap-1">
            <Button variant="outline" size="sm" onClick={() => preset('week')}>
              {t('admin.org.presetWeek')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => preset('month')}>
              {t('admin.org.presetMonth')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => preset('lastMonth')}>
              {t('admin.org.presetLastMonth')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => preset('quarter')}>
              {t('admin.org.presetQuarter')}
            </Button>
          </div>

          <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <MultiSelect
              label={t('admin.org.users')}
              emptyLabel={t('admin.org.allUsers')}
              options={(users.data?.data ?? []).map((u) => ({
                id: u.id,
                label: `${u.firstName} ${u.lastName}`,
              }))}
              selected={query.userIds}
              onChange={(ids) => setFilters({ userIds: joined(ids) })}
            />
            <MultiSelect
              label={t('admin.org.groups')}
              emptyLabel={t('admin.org.allGroups')}
              options={(groups.data?.data ?? []).map((g) => ({ id: g.id, label: g.name }))}
              selected={query.groupIds}
              onChange={(ids) => setFilters({ groupIds: joined(ids) })}
            />
            <MultiSelect
              label={t('admin.org.projects')}
              emptyLabel={t('admin.org.allProjects')}
              options={(projects.data?.data ?? []).map((p) => ({ id: p.id, label: p.name }))}
              selected={query.projectIds}
              onChange={(ids) => setFilters({ projectIds: joined(ids) })}
            />
            <MultiSelect
              label={t('admin.org.activities')}
              emptyLabel={t('admin.org.allActivities')}
              options={(activities.data?.data ?? []).map((a) => ({ id: a.id, label: a.name }))}
              selected={query.activityIds}
              onChange={(ids) => setFilters({ activityIds: joined(ids) })}
            />
          </div>

          {query.userIds.length ||
          query.groupIds.length ||
          query.projectIds.length ||
          query.activityIds.length ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setFilters({
                  userIds: undefined,
                  groupIds: undefined,
                  projectIds: undefined,
                  activityIds: undefined,
                })
              }
            >
              {t('admin.org.clear')}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {dashboard.isPending ? <Spinner /> : null}
      {dashboard.isError ? (
        <Alert variant="error">{describeError(dashboard.error, t)}</Alert>
      ) : null}

      {reporting ? (
        <NewReportDialog onClose={() => setReporting(false)} initialFilters={reportFilters} />
      ) : null}

      {dashboard.data ? (
        <Report
          data={dashboard.data}
          fmt={fmt}
          tab={tab}
          onTab={(next) => setFilters({ tab: next })}
          onDrillProject={(id) => setFilters({ projectIds: joined([id]) })}
          onDrillUser={(id) => setFilters({ userIds: joined([id]) })}
        />
      ) : null}
    </AdminPage>
  );
}

function Report({
  data,
  fmt,
  tab,
  onTab,
  onDrillProject,
  onDrillUser,
}: {
  data: OrgDashboard;
  fmt: ReturnType<typeof useFormat>;
  tab: 'overview' | 'users' | 'missing';
  onTab: (tab: 'overview' | 'users' | 'missing') => void;
  onDrillProject: (id: string) => void;
  onDrillUser: (id: string) => void;
}) {
  const { t } = useTranslation();

  const byProject: Datum[] = data.byProject.map((p) => ({
    key: p.projectId,
    label: p.name,
    minutes: p.minutes,
    color: p.color,
  }));
  const activityColors = seriesColors(data.byActivity.map((a) => a.activityId));
  const byActivity: Datum[] = data.byActivity.map((a) => ({
    key: a.activityId,
    label: a.name,
    minutes: a.minutes,
    color: activityColors.get(a.activityId),
  }));
  const groupColors = seriesColors(data.byGroup.map((g) => g.groupId));
  const byGroup: Datum[] = data.byGroup.map((g) => ({
    key: g.groupId,
    label: g.name,
    minutes: g.minutes,
    color: groupColors.get(g.groupId),
  }));
  const perDay: Datum[] = data.byDay.map((d) => ({
    key: d.date,
    label: d.date.slice(5),
    minutes: d.minutes,
  }));

  const summarize = (rows: Datum[]) =>
    rows
      .slice(0, 5)
      .map((r) => `${r.label} ${formatHours(r.minutes, fmt)}`)
      .join(', ');

  const delta = data.totalMinutes - data.previous.totalMinutes;

  return (
    <>
      <section
        aria-label={t('admin.org.totalHours')}
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
      >
        <Tile
          label={t('admin.org.totalHours')}
          value={formatHours(data.totalMinutes, fmt)}
          hint={
            data.previous.totalMinutes > 0
              ? `${delta >= 0 ? '+' : ''}${formatPercent(delta / data.previous.totalMinutes, fmt)}`
              : undefined
          }
        />
        <Tile label={t('admin.org.activeUsers')} value={String(data.activeUserCount)} />
        <Tile
          label={t('admin.org.avgPerUser')}
          value={formatHours(
            data.activeUserCount ? Math.round(data.totalMinutes / data.activeUserCount) : 0,
            fmt,
          )}
        />
        <Tile label={t('admin.org.missing')} value={String(data.usersWithoutTime.length)} />
      </section>

      <Tabs value={tab} onValueChange={(v) => onTab(v as 'overview' | 'users' | 'missing')}>
        <TabsList>
          <TabsTrigger value="overview">{t('admin.org.tabOverview')}</TabsTrigger>
          <TabsTrigger value="users">{t('admin.org.tabUsers')}</TabsTrigger>
          <TabsTrigger value="missing">{t('admin.org.tabMissing')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title={t('dashboard.byProject')}
              summary={t('dashboard.chartByProjectAlt', { summary: summarize(byProject) })}
              data={byProject}
              fmt={fmt}
              layout="horizontal"
              total={data.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
            <ChartCard
              title={t('dashboard.byActivity')}
              summary={t('dashboard.chartByActivityAlt', { summary: summarize(byActivity) })}
              data={byActivity}
              fmt={fmt}
              layout="horizontal"
              total={data.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
            <ChartCard
              title={t('admin.org.byGroup')}
              summary={t('dashboard.chartByProjectAlt', { summary: summarize(byGroup) })}
              data={byGroup}
              fmt={fmt}
              layout="horizontal"
              total={data.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
            <ChartCard
              title={t('admin.org.perDay')}
              summary={t('dashboard.chartPerDayAlt', {
                summary: summarize(perDay.filter((d) => d.minutes > 0)),
              })}
              data={perDay}
              fmt={fmt}
              layout="vertical"
              total={data.totalMinutes}
              emptyLabel={t('dashboard.noData')}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.byProject')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartTable
                rows={byProject}
                fmt={fmt}
                total={data.totalMinutes}
                labelHeader={t('time.project')}
                extra={(d) => (
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => onDrillProject(d.key)}
                  >
                    {t('admin.org.drillDown', { name: d.label })}
                  </button>
                )}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="pt-4">
          <DataTable
            caption={t('admin.org.byUser')}
            empty={t('dashboard.noData')}
            rows={data.byUser}
            rowKey={(u) => u.userId}
            columns={[
              {
                key: 'name',
                header: t('admin.org.users'),
                cell: (u) => (
                  <button
                    type="button"
                    className="text-left font-medium hover:underline"
                    onClick={() => onDrillUser(u.userId)}
                  >
                    {u.name}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {u.email}
                    </span>
                  </button>
                ),
              },
              {
                key: 'hours',
                header: t('app.hours'),
                numeric: true,
                cell: (u) => formatHours(u.minutes, fmt),
              },
              {
                key: 'share',
                header: t('dashboard.share'),
                numeric: true,
                cell: (u) =>
                  data.totalMinutes > 0 ? formatPercent(u.minutes / data.totalMinutes, fmt) : '–',
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="missing" className="pt-4">
          <DataTable
            caption={t('admin.org.tabMissing')}
            empty={t('admin.org.noMissing')}
            rows={data.usersWithoutTime}
            rowKey={(u) => u.userId}
            columns={[
              {
                key: 'name',
                header: t('admin.org.users'),
                cell: (u) => (
                  <span>
                    <span className="block font-medium">{u.name}</span>
                    <span className="block text-xs text-muted-foreground">{u.email}</span>
                  </span>
                ),
              },
              {
                key: 'timesheet',
                header: <span className="sr-only">{t('admin.actions')}</span>,
                numeric: true,
                cell: (u) => (
                  <Link
                    to="/admin/timesheets"
                    search={{ userId: u.userId }}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('admin.users.timesheet')}
                  </Link>
                ),
              },
            ]}
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs uppercase text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}
